import { Container, Graphics, Text } from 'pixi.js';
import { TILE_SIZE } from '../render/Tileset';
import { VIEW_W } from '../render/AreaView';

// TitleNode.tscn animation keyframes (seconds → alpha): 1s in / hold / 1s out.
const SINGLE_KEYS: [number, number][] = [
  [0, 0],
  [1, 1],
  [5, 1],
  [6, 0],
];
const TITLE_KEYS: [number, number][] = [
  [0, 0],
  [1, 1],
  [4, 1],
  [5, 0],
];
const SUBTITLE_KEYS: [number, number][] = [
  [4.8, 0],
  [5.8, 1],
  [9.8, 1],
  [10.8, 0],
];

class BannerLabel {
  readonly container = new Container();
  private bg = new Graphics();
  private label: Text;
  text: string | null = null; // sticky, for the dedupe check (Title.cs:21-22)
  private keys: [number, number][] = [];
  private time = 0;
  private playing = false;

  constructor(fontSize: number) {
    // TitleNode.tscn: white text on a black StyleBoxFlat (6px h / 2px v padding).
    this.label = new Text({
      text: '',
      style: { fontFamily: 'sans-serif', fontSize, fill: 0xffffff },
    });
    this.label.resolution = 2;
    this.container.addChild(this.bg, this.label);
    this.container.alpha = 0;
  }

  /** Set text + reposition (Title.cs adjustLabel): align by the object's tile x. */
  show(text: string, tileX: number, localX: number, localY: number, keys: [number, number][]): void {
    this.text = text;
    this.label.text = text;
    const w = this.label.width + 12;
    const h = this.label.height + 4;
    this.bg.clear().rect(0, 0, w, h).fill(0x000000);
    this.label.position.set(6, 2);
    const x =
      tileX < 8 ? localX : tileX > 16 ? localX + TILE_SIZE - w : localX + TILE_SIZE / 2 - w / 2;
    this.container.x = Math.max(0, Math.min(VIEW_W - w, x));
    this.container.y = localY;
    this.keys = keys;
    this.time = 0;
    this.playing = true;
    this.container.alpha = 0;
  }

  update(dt: number): void {
    if (!this.playing) return;
    this.time += dt;
    const keys = this.keys;
    if (this.time >= keys[keys.length - 1][0]) {
      this.container.alpha = keys[keys.length - 1][1];
      this.playing = false;
      return;
    }
    let a = keys[0][1];
    for (let i = 0; i < keys.length - 1; i++) {
      const [t0, a0] = keys[i];
      const [t1, a1] = keys[i + 1];
      if (this.time >= t0 && this.time < t1) {
        a = a0 + ((a1 - a0) * (this.time - t0)) / (t1 - t0);
        break;
      }
      if (this.time >= t1) a = a1;
    }
    this.container.alpha = a;
  }
}

/**
 * The Title object's screen-space banner (Title.cs + TitleNode.tscn): shows the
 * area's `Title`/`Subtitle` extra-data as black-boxed labels in the UI layer,
 * aligned by the object's tile x (<8 left, >16 right, else centre), the label
 * top at the object's own y (RectPosition.y = Position.y − 120 in the centred
 * control = Position.y from the top-left). Deduped against the current text.
 */
export class TitleBanner {
  readonly container = new Container();
  private title = new BannerLabel(21);
  private subtitle = new BannerLabel(13);

  constructor() {
    this.container.addChild(this.title.container, this.subtitle.container);
  }

  show(title: string | null, subtitle: string | null, tileX: number, tileY: number): void {
    if (title === this.title.text) title = null;
    if (subtitle === this.subtitle.text) subtitle = null;
    if (title == null && subtitle == null) return;
    const localX = tileX * TILE_SIZE;
    const localY = tileY * TILE_SIZE;
    const both = title != null && subtitle != null;
    if (title != null) this.title.show(title, tileX, localX, localY, both ? TITLE_KEYS : SINGLE_KEYS);
    if (subtitle != null)
      this.subtitle.show(subtitle, tileX, localX, localY, both ? SUBTITLE_KEYS : SINGLE_KEYS);
  }

  update(dt: number): void {
    this.title.update(dt);
    this.subtitle.update(dt);
  }

  destroy(): void {
    this.container.destroy({ children: true });
  }
}
